import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ConsoleLayout from '@/layouts/ConsoleLayout';
import { Button } from '@/components/ui/button';
import { 
  Activity, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  FileText,
  Users
} from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '@/config/api';

// BACKEND_URL imported from centralized config
const API = `${BACKEND_URL}/api`;

const AdminConsole = () => {
  const navigate = useNavigate();
  const [manuscripts, setManuscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState({
    activeSessions: 0,
    ttsPending: 0,
    processing: 0,
    errorRate: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await axios.get(`${API}/manuscripts`);
      const allManuscripts = response.data;
      setManuscripts(allManuscripts);

      // Calculate system health metrics
      const processing = allManuscripts.filter(m => 
        m.tts_generation_status === 'generating' || m.ingestion_status === 'processing'
      ).length;
      
      const ttsPending = allManuscripts.filter(m => 
        m.tts_needs_regeneration === true
      ).length;

      setSystemHealth({
        activeSessions: 0, // Would need sessions endpoint
        ttsPending,
        processing,
        errorRate: 0 // Would need error tracking
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(Math.round(num));
  };

  const totalEUs = manuscripts.reduce((sum, m) => sum + (m.total_engagement_units || 0), 0);
  const topBooksByEUs = [...manuscripts]
    .sort((a, b) => (b.total_engagement_units || 0) - (a.total_engagement_units || 0))
    .slice(0, 5);

  const getHealthColor = (value, threshold) => {
    if (value === 0) return 'text-green-600';
    if (value < threshold) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <ConsoleLayout pillarLabel="Admin Console">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        
        {/* System Health Summary */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">System Health</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-6 h-6 text-gray-400" />
                <span className={`text-2xl font-bold ${getHealthColor(systemHealth.activeSessions, 100)}`}>
                  {systemHealth.activeSessions}
                </span>
              </div>
              <p className="text-sm text-gray-600">Active Sessions</p>
              <p className="text-xs text-gray-400 mt-1">Last 15 min</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-6 h-6 text-gray-400" />
                <span className={`text-2xl font-bold ${getHealthColor(systemHealth.ttsPending, 5)}`}>
                  {systemHealth.ttsPending}
                </span>
              </div>
              <p className="text-sm text-gray-600">TTS Pending</p>
              <p className="text-xs text-gray-400 mt-1">Needs regeneration</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <FileText className="w-6 h-6 text-gray-400" />
                <span className={`text-2xl font-bold ${getHealthColor(systemHealth.processing, 10)}`}>
                  {systemHealth.processing}
                </span>
              </div>
              <p className="text-sm text-gray-600">Processing</p>
              <p className="text-xs text-gray-400 mt-1">Ingestion/TTS</p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-2">
                <AlertCircle className="w-6 h-6 text-gray-400" />
                <span className={`text-2xl font-bold ${systemHealth.errorRate > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {systemHealth.errorRate}%
                </span>
              </div>
              <p className="text-sm text-gray-600">Error Rate</p>
              <p className="text-xs text-gray-400 mt-1">Last 24 hours</p>
            </div>
          </div>
        </section>

        {/* Ingestion & TTS Queue */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Processing Queue</h2>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="divide-y divide-gray-200">
                {manuscripts
                  .filter(m => m.tts_generation_status === 'generating' || m.tts_needs_regeneration)
                  .slice(0, 10)
                  .map(manuscript => (
                    <div key={manuscript.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{manuscript.title}</h3>
                          <p className="text-sm text-gray-600 truncate">{manuscript.author}</p>
                        </div>
                        <div className="ml-4 flex items-center gap-3">
                          {manuscript.tts_generation_status === 'generating' ? (
                            <span className="flex items-center gap-1 text-yellow-600 text-sm font-medium">
                              <Clock className="w-4 h-4" />
                              Processing
                            </span>
                          ) : manuscript.tts_needs_regeneration ? (
                            <span className="flex items-center gap-1 text-orange-600 text-sm font-medium">
                              <AlertCircle className="w-4 h-4" />
                              Needs Regen
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                              <CheckCircle className="w-4 h-4" />
                              Complete
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                {manuscripts.filter(m => m.tts_generation_status === 'generating' || m.tts_needs_regeneration).length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p>All manuscripts processed</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Engagement & Economics */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Engagement & Economics</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* This Week Summary */}
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-6 text-white">
              <h3 className="text-lg font-semibold mb-4">This Week</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-purple-200">Total EUs Generated</span>
                  <span className="text-2xl font-bold">{formatNumber(totalEUs)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-purple-200">Avg. EU per Book</span>
                  <span className="text-xl font-bold">
                    {manuscripts.length > 0 ? formatNumber(totalEUs / manuscripts.length) : 0}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-purple-500">
                  <span className="text-purple-200">Est. Payout</span>
                  <span className="text-xl font-bold">${formatNumber(totalEUs * 0.001)}</span>
                </div>
              </div>
            </div>

            {/* Top 5 Books by EUs */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Top 5 Books by EUs</h3>
              <div className="space-y-3">
                {topBooksByEUs.map((manuscript, idx) => (
                  <div key={manuscript.id} className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                      ${idx === 0 ? 'bg-yellow-100 text-yellow-700' : 
                        idx === 1 ? 'bg-gray-100 text-gray-700' : 
                        idx === 2 ? 'bg-orange-100 text-orange-700' : 
                        'bg-purple-100 text-purple-700'}`}
                    >
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate text-sm">{manuscript.title}</p>
                    </div>
                    <div className="text-sm font-bold text-purple-600">
                      {formatNumber(manuscript.total_engagement_units || 0)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Content & Account Oversight */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Recent Activity</h2>
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="divide-y divide-gray-200">
              {manuscripts.slice(0, 10).map(manuscript => (
                <div key={manuscript.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">{manuscript.title}</h3>
                    <p className="text-sm text-gray-600">{manuscript.author}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Created: {new Date(manuscript.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="ml-4 flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => navigate('/author-console')}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
    </ConsoleLayout>
  );
};

export default AdminConsole;
